import { useCallback, useEffect, useState } from 'react';

import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ReactComponent as ArrowIcon } from '@assets/img/icons/arrow-down.svg';
import registrationLogo from 'assets/img/registration-logo.png';
import { FormikProps } from 'formik';
import { useAppSelector } from 'redux/store/store.types';
import { formatFileSize } from 'utils';
import * as Yup from 'yup';

import { useAppTerms } from 'modules/app-terms';
import { useAuthSession } from 'modules/auth-session';

import { Checkbox } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/styles';

import FormikForm from 'components/common/FormikForm';
import NibButton from 'components/common/NibButton/NibButton';
import NibCountrySelector from 'components/common/NibCountrySelector/NibCountrySelector';
import NibPasswordInput from 'components/common/NibPasswordInput';

import TermsModal from './TermsModal';

type InitValues = {
  first_name: string;
  last_name: string;
  email: string;
  username: string;
  password: string;
  rep_password: string;
  country_code: string;
  termsAccepted: boolean;
  documentType: string;
  region_code: string;
  idFile: File | null;
};

const initValues: InitValues = {
  first_name: '',
  last_name: '',
  email: '',
  username: '',
  password: '',
  rep_password: '',
  country_code: '',
  region_code: '',
  termsAccepted: false,
  documentType: 'none',
  idFile: null,
};

const ALLOWED_EXT = /(\.png|\.jpg|\.jpeg)$/i;
const MAX_SIZE = 1000 * (1000 * 8);

const validationSchema = Yup.object().shape({
  first_name: Yup.string().required('Required'),
  last_name: Yup.string().required('Required'),
  email: Yup.string().email('Invalid email').required('Required'),
  username: Yup.string().required('Required'),
  password: Yup.string().required('Required'),
  rep_password: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Required'),
  country_code: Yup.string().required('Country is required'),
  termsAccepted: Yup.boolean().oneOf([true], 'You must accept the terms').required('Required'),
  documentType: Yup.string(),
  idFile: Yup.mixed().when('documentType', {
    is: (documentType: any) => documentType !== 'none',
    then: Yup.mixed()
      .required('ID file is required')
      .test('fileSize', 'File too large', (value) => value && value.size <= MAX_SIZE)
      .test('fileFormat', 'Unsupported format', (value) => value && ALLOWED_EXT.test(value.name)),
  }),
});

const RegistrationPage = () => {
  const history = useHistory();
  const { search } = useLocation();
  const { isLoading } = useAppSelector((state) => state.authSession);
  const { brandingSettings } = useAppSelector((state) => state.platform);

  const { signupHandler } = useAuthSession();
  const { fetchTermsByCodeHandler } = useAppTerms();

  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  const params = new URLSearchParams(search);
  const invitationCode = params.get('invite') || '';

  const submitHandler = useCallback(
    async (values: InitValues) => {
      const result = await signupHandler({
        login: values.email,
        first_name: values.first_name,
        last_name: values.last_name,
        username: values.username,
        email: values.email,
        password: values.password,
        country_code: values.country_code,
        region_code: values.region_code,
        invitation_code: invitationCode,
        id_document: values.idFile,
      });

      if (result) {
        toast.success('Registration was successful, please Sign In !');
        history.push('/login');
      }
    },
    [invitationCode],
  );

  const useInputStyles = makeStyles({
    input: {
      height: '36px',
    },
    select: {
      height: '28px',
      marginTop: '8px',
    },
    root: {
      '& .MuiFormLabel-filled': {
        marginTop: '0px !important',
      },
      '& .Mui-focused': {
        marginTop: '0px !important',
      },
      '& .MuiFormLabel-root': {
        color: '#898989',
      },
      '& .MuiInputLabel-root': {
        color: '#898989',
      },
      '& .MuiInputLabel-outlined': {
        color: '#898989',
        marginTop: '9px',
      },
      '& fieldset': {
        borderRadius: '12px',
      },
    },
  });

  const InputStyles = useInputStyles();

  useEffect(() => {
    if (invitationCode) {
      fetchTermsByCodeHandler({
        invitation_code: invitationCode,
      });
    }
  }, [invitationCode]);

  return (
    <div className="flex items-center justify-center w-full mt-[120px]">
      <div className="flex items-center justify-center w-full h-full">
        <div className="flex flex-col items-center justify-start rounded-xl min-h-[700px] w-[360px] sm:w-[630px] shadow-appShadow-7 bg-appGradient-2">
          <img className="mt-[-100px]" src={registrationLogo} alt="logo" />

          <p className="uppercase text-[22px] font-semibold pb-5 mt-[-16px]">
            Create your {brandingSettings.company_name} account
          </p>

          <FormikForm
            formClassName="flex flex-col items-center justify-center w-full"
            validationSchema={validationSchema}
            initialValues={initValues}
            onSubmit={submitHandler}
            data={initValues}
            enableReinitialize
          >
            {(formik: FormikProps<InitValues>) => (
              <div className="flex flex-col justify-center w-full">
                <div className="md:[&>div]:basis-[45%] [&>div]:m-[10px] [&>div]:basis-[90%] w-full flex items-center justify-center ml-0 pl-0 md:items-start md:justify-start flex-wrap md:ml-1 md:pl-2">
                  <TextField
                    variant="outlined"
                    name="first_name"
                    value={formik.values.first_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.first_name && Boolean(formik.errors.first_name)}
                    label="First Name"
                    classes={InputStyles}
                    required
                  />

                  <TextField
                    variant="outlined"
                    name="last_name"
                    value={formik.values.last_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.last_name && Boolean(formik.errors.last_name)}
                    label="Last Name"
                    classes={InputStyles}
                    required
                  />

                  <TextField
                    variant="outlined"
                    name="username"
                    value={formik.values.username}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.username && Boolean(formik.errors.username)}
                    label="Username"
                    classes={InputStyles}
                    required
                  />

                  <TextField
                    variant="outlined"
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    label="Email"
                    type="email"
                    classes={InputStyles}
                    required
                  />

                  <NibPasswordInput
                    value={formik.values.password}
                    onChange={(value: string) => formik.setFieldValue('password', value)}
                    // @ts-ignore
                    onBlur={() => formik.setFieldTouched('password', true)}
                    error={formik.touched.password && Boolean(formik.errors.password)}
                    label="Set a password"
                    classes={InputStyles}
                    required
                  />

                  <NibPasswordInput
                    value={formik.values.rep_password}
                    onChange={(value: string) => formik.setFieldValue('rep_password', value)}
                    // @ts-ignore
                    onBlur={() => formik.setFieldTouched('rep_password', true)}
                    error={formik.touched.rep_password && Boolean(formik.errors.rep_password)}
                    label="Repeat password"
                    classes={InputStyles}
                    required
                  />

                  <NibCountrySelector
                    // @ts-ignore
                    onChange={(data) => {
                      formik.setFieldValue('country_code', data.country?.code || null);
                      formik.setFieldValue('region_code', data.region?.code || null);
                    }}
                    // @ts-ignore
                    countryError={formik.touched.country_code && formik.errors.country_code}
                    countryReq={true}
                    classes={InputStyles}
                  />

                  <FormControl variant="outlined" classes={InputStyles}>
                    <InputLabel classes={InputStyles}>Select ID type</InputLabel>
                    <Select
                      name="documentType"
                      value={formik.values.documentType}
                      onChange={formik.handleChange}
                      classes={InputStyles}
                      label="Select ID type"
                      IconComponent={ArrowIcon}
                    >
                      <MenuItem value="none">None</MenuItem>
                      <MenuItem value="id">ID</MenuItem>
                      <MenuItem value="passport">Passport</MenuItem>
                      <MenuItem value="drivers_license">Drivers license</MenuItem>
                    </Select>
                    {formik.values.documentType !== 'none' && (
                      <div className="flex items-center justify-between mt-[10px]">
                        {formik.values.idFile && <span>Size: {formatFileSize(formik?.values?.idFile?.size || 0)}</span>}
                        <Button variant="contained" color="primary" component="label">
                          Upload
                          <input
                            type="file"
                            hidden
                            accept=".png, .jpg, .jpeg"
                            onChange={(e) => {
                              // @ts-ignore
                              const file = e?.target?.files[0] || null;
                              formik.setFieldValue('idFile', file);
                            }}
                          />
                        </Button>
                        {formik.touched.idFile && formik.errors.idFile && (
                          <div className="text-red-700 pl-[24px]">{formik.errors.idFile}</div>
                        )}
                      </div>
                    )}
                  </FormControl>
                </div>

                <div className="flex flex-col">
                  <div className="w-[60%] flex items-center mt-[10px] ml-[12px] md:w-full">
                    <Checkbox
                      checked={formik.values.termsAccepted}
                      onChange={(e) => formik.setFieldValue('termsAccepted', e.target.checked)}
                    />
                    <div className="text-white underline cursor-pointer">
                      <span onClick={() => setIsTermsModalOpen(true)}>I have read and accept Terms and Conditions</span>
                    </div>
                  </div>
                  {formik.touched.termsAccepted && formik.errors.termsAccepted && (
                    <div className="text-red-700 pl-[24px]">{formik.errors.termsAccepted}</div>
                  )}
                </div>

                <div className="w-full flex justify-center">
                  {/*  @ts-ignore */}
                  <NibButton
                    type="submit"
                    className="!w-[340px] !h-[60px] !bg-appThemeColor !text-appThemeTextColor !rounded-[10px] !m-[40px] !text-[18px] !uppercase"
                    mui
                    variant="outlined"
                    size="large"
                    label="Register"
                    disabled={isLoading}
                  />
                </div>
              </div>
            )}
          </FormikForm>

          <p className="text-white/30 pb-[24px]">
            Already have an account?{' '}
            <a className="text-white underline" href="/login">
              Sign In
            </a>
          </p>
        </div>
      </div>
      <TermsModal isModalOpen={isTermsModalOpen} onClose={() => setIsTermsModalOpen(false)} />
    </div>
  );
};

export default RegistrationPage;
